import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, FolderOpen, Trash2, Download, RefreshCw, AlertCircle, X, Search, File } from 'lucide-react';
import { filesApi } from '../../api/client';
import { sanitizeError } from '../../utils/errorMessages';
import './StorageManager.css';

// ─────────────────────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────────────────────
const formatBytes = (bytes, dec = 1) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dec))} ${sizes[i]}`;
};

const getFileType = (name = '') => {
  const ext = name.split('.').pop().toLowerCase();
  const types = {
    gsi: { color: '#818cf8', bg: 'rgba(129,140,248,0.12)' },
    dxf: { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
    csv: { color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
    txt: { color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
    pdf: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
    zip: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    rar: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    shp: { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
  };
  return { label: (ext || 'FIC').toUpperCase().slice(0, 3), ...(types[ext] || { color: '#475569', bg: 'rgba(71,85,105,0.12)' }) };
};

// ─────────────────────────────────────────────────────────────
// STORAGE GAUGE (SVG ring)
// ─────────────────────────────────────────────────────────────
const StorageGauge = ({ pct }) => {
  const r = 42;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const color = pct >= 90 ? '#ef4444' : pct >= 70 ? '#fbbf24' : '#818cf8';
  const cls = pct >= 90 ? 'sm-gauge-pct--danger' : pct >= 70 ? 'sm-gauge-pct--warning' : 'sm-gauge-pct--ok';

  return (
    <div className="sm-gauge-wrap">
      <svg width="100" height="100" className="sm-gauge-svg">
        <circle className="sm-gauge-bg" cx="50" cy="50" r={r} strokeWidth="6" />
        <circle
          className="sm-gauge-fill"
          cx="50" cy="50" r={r} strokeWidth="6"
          stroke={color}
          strokeDasharray={circ}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="sm-gauge-text">
        <span className={`sm-gauge-pct ${cls}`}>{pct.toFixed(0)}%</span>
        <span className="sm-gauge-label">Utilisé</span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SKELETON ROW
// ─────────────────────────────────────────────────────────────
const SkeletonRow = () => (
  <div className="sm-skeleton-row">
    <div className="sm-sk" style={{ width: 36, height: 36, borderRadius: 9 }} />
    <div style={{ flex: 1 }}>
      <div className="sm-sk" style={{ height: 13, width: '55%', marginBottom: 6 }} />
      <div className="sm-sk" style={{ height: 10, width: '30%' }} />
    </div>
    <div className="sm-sk" style={{ width: 60, height: 28, borderRadius: 7 }} />
  </div>
);

// ─────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
export default function StorageManager() {
  const [files,        setFiles]        = useState([]);
  const [quota,        setQuota]        = useState({ used_bytes: 0, limit_bytes: 0 });
  const [isLoading,    setIsLoading]    = useState(true);
  const [uploadPct,    setUploadPct]    = useState(0);
  const [isUploading,  setIsUploading]  = useState(false);
  const [deleting,     setDeleting]     = useState(null);
  const [confirmId,    setConfirmId]    = useState(null); // id du fichier en attente de suppression
  const [toast,        setToast]        = useState(null); // { msg, type } — notification auto-dismiss
  const [dragOver,     setDragOver]     = useState(false);
  const [search,       setSearch]       = useState('');
  const fileRef    = useRef(null);
  const toastTimer = useRef(null);

  // ── Notification toast (auto-dismiss 4 s pour succès, persistant pour erreur) ──
  const notify = useCallback((msg, type = 'error') => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type });
    if (type === 'success' || type === 'info') {
      toastTimer.current = setTimeout(() => setToast(null), 4000);
    }
  }, []);

  const dismissToast = () => { clearTimeout(toastTimer.current); setToast(null); };

  // ── Fetch data ──────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await filesApi.list();
      const payload = data?.data ?? data;
      setFiles(payload?.files ?? []);
      setQuota(payload?.storage ?? payload?.storage_status ?? { used_bytes: 0, limit_bytes: 0 });
    } catch {
      notify('Impossible de charger vos fichiers. Vérifiez votre connexion.');
    } finally {
      setIsLoading(false);
    }
  }, [notify]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Upload ──────────────────────────────────────────────────
  const doUpload = async (file) => {
    if (!file) return;
    const available = quota.limit_bytes - quota.used_bytes;
    if (quota.limit_bytes > 0 && file.size > available) {
      notify(`Espace insuffisant. Disponible : ${formatBytes(available)}`, 'warning');
      return;
    }
    dismissToast(); setUploadPct(0); setIsUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await filesApi.upload(fd, {
        onUploadProgress: (e) => setUploadPct(Math.round((e.loaded * 100) / e.total)),
      });
      notify('Fichier ajouté avec succès.', 'success');
      await fetchData();
    } catch (err) {
      const status  = err?.response?.status;
      const rawMsg  = err?.response?.data?.message || err?.response?.data?.error || err?.message || '';
      // Doublon détecté par le status HTTP ou par le message PostgreSQL brut
      const isDupe  = status === 409 || /duplicate key|unique constraint/i.test(rawMsg);
      if (isDupe) {
        notify('Un fichier avec ce nom existe déjà dans votre espace. Renommez-le ou supprimez l\'existant.', 'warning');
      } else if (status === 403) {
        notify('Quota de stockage dépassé. Supprimez des fichiers pour libérer de l\'espace.', 'warning');
      } else {
        notify(sanitizeError(rawMsg, "L'upload a échoué. Réessayez."));
      }
    } finally {
      setIsUploading(false); setUploadPct(0);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleFileInput = (e) => doUpload(e.target.files[0]);
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files[0]); };

  // ── Delete — confirmation inline ─────────────────────────────
  const requestDelete = (id) => setConfirmId(id);
  const cancelDelete  = ()   => setConfirmId(null);

  const confirmDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    setDeleting(id);
    try {
      await filesApi.remove(id);
      setFiles(prev => prev.filter(f => f.id !== id));
      notify('Fichier supprimé.', 'info');
    } catch (err) {
      const rawMsg = err?.response?.data?.message || err?.message || '';
      notify(sanitizeError(rawMsg, 'Impossible de supprimer ce fichier. Réessayez.'));
    } finally {
      setDeleting(null);
    }
  };

  // ── Derived values ──────────────────────────────────────────
  const pct = quota.limit_bytes > 0
    ? parseFloat(((quota.used_bytes / quota.limit_bytes) * 100).toFixed(2))
    : 0;

  const filtered = files.filter(f =>
    !search || (f.name || f.file_name || '').toLowerCase().includes(search.toLowerCase())
  );

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  return (
    <div className="sm-page">

      {/* ── Header ── */}
      <div className="sm-header">
        <div className="sm-header-left">
          <div className="sm-icon-badge">
            <FolderOpen size={20} />
          </div>
          <div className="sm-title">
            <h1>Mon Espace Fichiers</h1>
            <p>Stockage personnel sécurisé — GSI, DXF, CSV…</p>
          </div>
        </div>
        <button className="sm-refresh-btn" onClick={fetchData} disabled={isLoading}>
          <RefreshCw size={13} className={isLoading ? 'sm-spin' : ''} />
          Actualiser
        </button>
      </div>

      {/* ── Toast notification ── */}
      {toast && (
        <div className={`sm-toast sm-toast--${toast.type}`}>
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{toast.msg}</span>
          <button className="sm-toast-close" onClick={dismissToast}><X size={13} /></button>
        </div>
      )}

      {/* ── Confirmation suppression inline ── */}
      {confirmId && (
        <div className="sm-confirm">
          <span>Supprimer ce fichier définitivement ?</span>
          <div className="sm-confirm-actions">
            <button className="sm-confirm-btn sm-confirm-btn--cancel" onClick={cancelDelete}>
              Annuler
            </button>
            <button className="sm-confirm-btn sm-confirm-btn--delete" onClick={confirmDelete}>
              Supprimer
            </button>
          </div>
        </div>
      )}

      {/* ── Top grid (Quota + Upload) ── */}
      <div className="sm-top">

        {/* Quota card */}
        <div className="sm-panel">
          <div className="sm-panel-header">
            <div className="sm-panel-icon sm-panel-icon--purple">
              <FolderOpen size={13} />
            </div>
            <span className="sm-panel-title">Quota de stockage</span>
          </div>
          <div className="sm-panel-body">
            <div className="sm-quota-center">
              <StorageGauge pct={pct} />
              <div className="sm-quota-stats">
                <div className="sm-quota-stat">
                  <div className="sm-quota-stat-val">{formatBytes(quota.used_bytes)}</div>
                  <div className="sm-quota-stat-key">Utilisé</div>
                </div>
                <div className="sm-quota-stat">
                  <div className="sm-quota-stat-val">{formatBytes(quota.limit_bytes)}</div>
                  <div className="sm-quota-stat-key">Limite</div>
                </div>
                <div className="sm-quota-stat">
                  <div className="sm-quota-stat-val">{formatBytes(quota.limit_bytes - quota.used_bytes)}</div>
                  <div className="sm-quota-stat-key">Libre</div>
                </div>
              </div>
              {pct >= 100 && (
                <div className="sm-quota-full">
                  <AlertCircle size={14} />
                  Stockage plein — supprimez des fichiers.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upload card */}
        <div className="sm-panel">
          <div className="sm-panel-header">
            <div className="sm-panel-icon sm-panel-icon--cyan">
              <Upload size={13} />
            </div>
            <span className="sm-panel-title">Importer un fichier</span>
          </div>
          <div className="sm-panel-body">

            {/* Drag & drop zone */}
            <div
              className={`sm-dropzone${dragOver ? ' sm-dropzone--over' : ''}${pct >= 100 ? ' sm-dropzone--disabled' : ''}`}
              onDragOver={e => { e.preventDefault(); if (pct < 100) setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={pct < 100 ? handleDrop : e => e.preventDefault()}
              style={pct >= 100 ? { opacity: 0.4, pointerEvents: 'none' } : {}}
            >
              <input
                ref={fileRef}
                type="file"
                onChange={handleFileInput}
                disabled={isUploading || pct >= 100}
              />
              <div className="sm-dropzone-icon">
                <Upload size={18} />
              </div>
              <div className="sm-dropzone-title">
                Glissez votre fichier ici ou <strong>cliquez pour parcourir</strong>
              </div>
              <div className="sm-dropzone-hint">
                GSI, DXF, CSV, TXT, PDF, ZIP — max {formatBytes(quota.limit_bytes - quota.used_bytes)} disponible
              </div>
            </div>

            {/* Upload progress */}
            {(isUploading || uploadPct > 0) && (
              <div className="sm-progress-wrap">
                <div className="sm-progress-label">
                  <span>Upload en cours…</span>
                  <span>{uploadPct}%</span>
                </div>
                <div className="sm-progress-bar">
                  <div className="sm-progress-fill" style={{ width: `${uploadPct}%` }} />
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ── File list ── */}
      <div className="sm-panel">
        <div className="sm-files-header">
          <div className="sm-files-title">
            <File size={14} />
            Mes fichiers
            <span className="sm-files-count">{files.length}</span>
          </div>
          <div className="sm-search">
            <Search size={12} color="#334155" />
            <input
              className="sm-search-input"
              placeholder="Rechercher…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="sm-files-list">
          {isLoading
            ? [1,2,3].map(i => <SkeletonRow key={i} />)
            : filtered.length > 0
              ? filtered.map(file => {
                  const name = file.name || file.file_name || 'Fichier sans nom';
                  const { label, color, bg } = getFileType(name);
                  const uploadedAt = file.uploaded_at || file.created_at;
                  return (
                    <div key={file.id} className="sm-file-row">
                      <div className="sm-file-type" style={{ background: bg, color }}>
                        {label}
                      </div>
                      <div className="sm-file-info">
                        <a
                          href={file.file_url || file.url || '#'}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="sm-file-name"
                        >
                          {name}
                        </a>
                        <div className="sm-file-meta">
                          {formatBytes(file.file_size || file.size)}
                          {uploadedAt && ` · ${new Date(uploadedAt).toLocaleDateString('fr-FR')}`}
                        </div>
                      </div>
                      <div className="sm-file-actions">
                        {(file.file_url || file.url) && (
                          <a
                            href={file.file_url || file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="sm-file-btn"
                            title="Télécharger"
                          >
                            <Download size={13} />
                          </a>
                        )}
                        <button
                          className="sm-file-btn sm-file-btn--danger"
                          onClick={() => requestDelete(file.id)}
                          disabled={deleting === file.id}
                          title="Supprimer"
                        >
                          {deleting === file.id
                            ? <div className="sm-spin" />
                            : <Trash2 size={13} />
                          }
                        </button>
                      </div>
                    </div>
                  );
                })
              : (
                <div className="sm-empty">
                  <div className="sm-empty-icon"><FolderOpen size={22} /></div>
                  <h4>{search ? 'Aucun fichier trouvé' : 'Aucun fichier pour l\'instant'}</h4>
                  <p>{search ? `Aucun résultat pour "${search}"` : 'Importez votre premier fichier ci-dessus.'}</p>
                </div>
              )
          }
        </div>
      </div>
    </div>
  );
}
